import { useMemo, useState } from "react";
import { appLink } from "./bookmaker-link.js";

export interface PlacementRecommendation {
  readonly signalId: string;
  readonly description: string;
  readonly bookmaker: string;
  readonly line: number | null;
  readonly price: number;
  readonly minimumOdds: number;
  readonly units: number;
  readonly stakeCents: number;
  /** Anything but LIVE means the rule is not in the live set, so placement is
   * refused unless the operator explicitly acknowledges it. */
  readonly executionMode?: string;
  /** Live quotes for this exact bet, used to prefill the price when the
   * operator switches book. */
  readonly offers?: readonly {
    readonly bookmaker: string;
    readonly price: number;
    readonly line: number | null;
    readonly role: string;
    /** The book's page for this game, from the odds feed. */
    readonly url?: string | null;
  }[];
}

/** The Australian books the API accepts. Kept in step with the enum in
 * apps/api/src/routes/bets.ts by hand: the dashboard has no workspace deps. */
import { routeBookmakers } from "../book-routing.js";

export const EXECUTION_BOOKMAKERS = [
  "sportsbet",
  "bet365",
  "tab",
  "pointsbet",
  "ladbrokes",
  "dabble",
  "picklebet",
  "betr",
  "nextbet",
  "unibet",
  "betright",
] as const;

export interface BookAccount {
  readonly bookmaker: string;
  readonly status: "ACTIVE" | "LIMITED" | "CLOSED";
  readonly availableCents: number;
  readonly turnoverShare: number;
  readonly scarcity: number;
}

/**
 * Every execution book ranked for this bet. Shared by the dialog and the Home
 * queue so both name the same book. A book with no account row still appears,
 * because betting somewhere we hold no balance is a deposit prompt, not a
 * thing to hide.
 */
export function routeRecommendation(
  recommendation: Pick<
    PlacementRecommendation,
    "offers" | "line" | "minimumOdds"
  >,
  books: readonly BookAccount[],
  stakeCents: number,
) {
  const byBook = new Map(books.map((book) => [book.bookmaker, book]));
  return routeBookmakers(
    EXECUTION_BOOKMAKERS.map((option) => {
      const quote = (recommendation.offers ?? []).find(
        (offer) =>
          offer.bookmaker === option && offer.line === recommendation.line,
      );
      const account = byBook.get(option);
      return {
        bookmaker: option,
        price: quote?.price ?? 0,
        availableCents: account?.availableCents ?? 0,
        status: account?.status ?? ("ACTIVE" as const),
      };
    }),
    {
      minimumAcceptableOdds: recommendation.minimumOdds,
      stakeCents: Number.isFinite(stakeCents) ? stakeCents : 0,
    },
  );
}

/**
 * The book to send the operator to: the router's pick, falling back to the
 * alert's best-price book when the router has nothing usable. Board, queue and
 * dialog all link here so they never name different books.
 */
export function recommendedBook(
  recommendation: Pick<
    PlacementRecommendation,
    "offers" | "line" | "minimumOdds" | "bookmaker" | "price" | "stakeCents"
  >,
  books: readonly BookAccount[],
): { readonly bookmaker: string; readonly price: number } {
  const routed = routeRecommendation(
    recommendation,
    books,
    recommendation.stakeCents,
  ).find((option) => option.recommended && option.price > 0);
  return routed === undefined
    ? { bookmaker: recommendation.bookmaker, price: recommendation.price }
    : { bookmaker: routed.bookmaker, price: routed.price };
}

export function PlaceBetDialog({
  recommendation,
  books = [],
  onClose,
  onConfirm,
}: {
  recommendation: PlacementRecommendation;
  /** Account state per book, for routing. Empty falls back to the
   * plain list, so the dialog still works if the accounts read fails. */
  books?: readonly BookAccount[];
  onClose: () => void;
  onConfirm: (input: {
    actualLine: number | null;
    actualPrice: number;
    stakeCents: number;
    bookmaker: string;
    allowNonLive?: boolean;
  }) => Promise<void> | void;
}) {
  const [line, setLine] = useState(
    recommendation.line === null ? "" : String(recommendation.line),
  );
  const [price, setPrice] = useState(String(recommendation.price));
  const [bookmaker, setBookmaker] = useState(recommendation.bookmaker);
  const [stake, setStake] = useState(String(recommendation.stakeCents / 100));
  // Research rules still produce recommendations, and their alerts used to
  // carry a working bookmaker link. Money got placed off one, and the ledger
  // had no way to record it. Acknowledging here is the fallback that keeps
  // real exposure tracked rather than invisible.
  const isResearchOnly =
    recommendation.executionMode !== undefined &&
    recommendation.executionMode !== "LIVE";
  const [acknowledgedNonLive, setAcknowledgedNonLive] = useState(false);
  // Opened the book from here: on return, logging is one tap (Moss, 3 Nov
  // 2026: "place the bet and log it 1 click from the notification").
  const [openedBook, setOpenedBook] = useState(false);
  const [submission, setSubmission] = useState<
    "idle" | "submitting" | "succeeded"
  >("idle");
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  // Straight to the market from a phone alert, then back here to log it.
  const offerUrl =
    (recommendation.offers ?? []).find(
      (offer) =>
        offer.bookmaker === bookmaker &&
        typeof offer.url === "string" &&
        offer.url.startsWith("https://"),
    )?.url ?? null;
  const bookUrl = offerUrl === null ? null : appLink(offerUrl);
  const actualPrice = Number(price);
  const stakeCents = Math.round(Number(stake) * 100);
  const routed = useMemo(
    () => routeRecommendation(recommendation, books, stakeCents),
    [books, recommendation, stakeCents],
  );
  const suggestion = routed.find((option) => option.recommended);
  const withinAllocation = stakeCents <= recommendation.stakeCents;
  const canPlace =
    Number.isFinite(actualPrice) &&
    actualPrice >= recommendation.minimumOdds &&
    Number.isSafeInteger(stakeCents) &&
    stakeCents > 0 &&
    withinAllocation &&
    (!isResearchOnly || acknowledgedNonLive) &&
    submission === "idle";
  const warning = useMemo(
    () =>
      !withinAllocation
        ? "Stake exceeds allocated risk"
        : actualPrice < recommendation.minimumOdds
          ? "Below minimum — do not place"
          : "Price meets minimum",
    [actualPrice, recommendation.minimumOdds, withinAllocation],
  );
  const closeUnlessSubmitting = () => {
    if (submission !== "submitting") onClose();
  };
  const submit = async () => {
    if (!canPlace) return;
    setSubmission("submitting");
    setSubmissionError(null);
    try {
      await onConfirm({
        actualLine: line === "" ? null : Number(line),
        actualPrice,
        stakeCents,
        bookmaker,
        ...(isResearchOnly ? { allowNonLive: true } : {}),
      });
      setSubmission("succeeded");
    } catch (error) {
      setSubmissionError(
        error instanceof Error
          ? error.message
          : "The placement could not be logged. Check your connection and try again.",
      );
      setSubmission("idle");
    }
  };
  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={closeUnlessSubmitting}
    >
      <section
        className="dialog card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="placement-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">Click-to-log ledger</span>
            <h2 id="placement-title">{recommendation.description}</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label="Close placement"
            disabled={submission === "submitting"}
            onClick={closeUnlessSubmitting}
          >
            ×
          </button>
        </header>
        <div className="dialog-body">
          {openedBook && submission !== "succeeded" ? (
            <button
              type="button"
              className="button primary quick-log"
              disabled={!canPlace}
              onClick={() => void submit()}
            >
              {`Placed at ${price} · $${stake} — log it`}
            </button>
          ) : null}
          {bookUrl === null ? null : (
            <a
              className={`button ${openedBook ? "secondary" : "primary"}`}
              href={bookUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpenedBook(true)}
            >
              Open {bookmaker} ↗
            </a>
          )}
          {openedBook ? (
            <small className="routing-hint">
              Got a different price or stake? Change it below first.
            </small>
          ) : null}
          <label>
            Bookmaker
            <select
              aria-label="Bookmaker"
              value={bookmaker}
              onChange={(event) => {
                const chosen = event.target.value;
                setBookmaker(chosen);
                // Switching book usually means a different price, so offer the
                // one we hold rather than leaving the old book's number behind.
                const quote = (recommendation.offers ?? []).find(
                  (offer) =>
                    offer.bookmaker === chosen &&
                    offer.line === recommendation.line,
                );
                if (quote !== undefined) setPrice(String(quote.price));
              }}
            >
              {routed.map((option) => (
                <option key={option.bookmaker} value={option.bookmaker}>
                  {option.recommended ? "★ " : ""}
                  {option.bookmaker}
                  {option.price === 0 ? "" : ` — ${option.price.toFixed(2)}`}
                  {` · ${option.reason}`}
                </option>
              ))}
            </select>
            {/* Said in the open, because the whole point is that the best
                price is sometimes the wrong bet: two books price three
                quarters of the prop market and spending them is what ends an
                operation. A give-up is only ever a few cents, and never below
                the frozen minimum. */}
            {suggestion === undefined ||
            suggestion.bookmaker === bookmaker ? null : (
              <small className="routing-hint">
                {suggestion.bookmaker} is the pick
                {suggestion.costCents === 0
                  ? ""
                  : ` for ${(suggestion.costCents / 100).toFixed(2)}`}
                {" — "}
                {suggestion.reason}
              </small>
            )}
          </label>
          <label>
            Actual line
            <input
              aria-label="Actual line"
              inputMode="decimal"
              value={line}
              onChange={(event) => setLine(event.target.value)}
            />
          </label>
          <label>
            Actual price
            <input
              aria-label="Actual price"
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
          </label>
          <label>
            Minimum acceptable odds
            <output>{recommendation.minimumOdds.toFixed(2)}</output>
          </label>
          <label>
            Stake dollars
            <input
              aria-label="Stake dollars"
              inputMode="decimal"
              value={stake}
              onChange={(event) => setStake(event.target.value)}
            />
          </label>
          {isResearchOnly ? (
            <label className="value-check fail">
              <input
                type="checkbox"
                checked={acknowledgedNonLive}
                onChange={(event) =>
                  setAcknowledgedNonLive(event.target.checked)
                }
              />
              <strong>This rule is not in the live set</strong>
              <span>
                The system did not authorise this bet. Tick only if you have
                already placed it yourself, so the ledger can track the money.
                It will be recorded as placed outside the live rules.
              </span>
            </label>
          ) : null}
          <div className={`value-check ${canPlace ? "pass" : "fail"}`}>
            <strong>{warning}</strong>
            <span>
              {recommendation.units}u · A${(stakeCents / 100 || 0).toFixed(2)} ·
              risk caps rechecked on submit
            </span>
          </div>
          {submissionError === null ? null : (
            <div className="value-check fail" role="alert">
              <strong>Placement not logged</strong>
              <span>{submissionError}</span>
            </div>
          )}
          {submission !== "succeeded" ? null : (
            <div
              className="value-check pass"
              role="status"
              aria-label="Placement result"
            >
              <strong>Placement logged</strong>
              <span>The bet has been added to the live ledger.</span>
            </div>
          )}
        </div>
        <footer>
          {submission === "succeeded" ? (
            <button type="button" className="button primary" onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                className="button secondary"
                disabled={submission === "submitting"}
                onClick={closeUnlessSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button primary"
                disabled={!canPlace}
                onClick={() => void submit()}
              >
                {submission === "submitting"
                  ? "Logging placement…"
                  : "Confirm and log placement"}
              </button>
            </>
          )}
        </footer>
      </section>
    </div>
  );
}
